import React, { useEffect, useState } from "react";
import { addWordToDictionary } from "./api/dictionaryApi";
import { UserOptions as UserOptionsAPI } from "./UserOptions";
import { ExplorationResult } from "../../types/chat";

interface WordExplorerProps {
  text: string;
  clickedWord: string;
  onClose?: () => void;
}

const baseUrl: string = process.env.REACT_APP_BACKEND_URL || "";

const WordExplorer = ({ text, clickedWord, onClose }: WordExplorerProps) => {
  const [explorationResult, setExplorationResult] = useState<ExplorationResult | null>(null);
  const [addingToDictionary, setAddingToDictionary] = useState<boolean>(false);
  const [addedToDictionary, setAddedToDictionary] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!text) return;

    let ignore = false; // prevents double state update in Strict Mode

    const explore = async () => {
      try {
        setError(null);
        const result = await exploreWord(text);
        if (!ignore) {
          setExplorationResult(result);
          setAddedToDictionary(false);
        }
      } catch (error: any) {
        if (!ignore) setError(error.message || "Failed to explore word");
      }
    };

    explore();

    return () => {
      ignore = true; // ignore second Strict Mode call
    };
  }, [text]); // ✅ trigger ONLY when text changes

  // Minimal fetcher for word exploration
  const exploreWord = async (word: string): Promise<ExplorationResult> => {
    const accessToken = localStorage.getItem("accessToken");
    const userOptionsString = localStorage.getItem("userOptions");
    let userNativeLanguage: string | undefined;
    
    if (userOptionsString) {
      userNativeLanguage = JSON.parse(userOptionsString).user_native_language;
    } else {
      const userOptions = await UserOptionsAPI();
      localStorage.setItem("userOptions", JSON.stringify(userOptions));
      userNativeLanguage = userOptions.user_native_language;
    }

    const conversationLanguage = localStorage.getItem("conversation_language");
    if (!conversationLanguage) throw new Error("Please select a conversation first.");

    const payload = {
      user_prompt: word,
      user_native_language: userNativeLanguage,
      conversation_language: conversationLanguage,
    };

    const response = await fetch(`${baseUrl}/word_explorer/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw new Error("Failed to get a response from the API");

    return response.json();
  };

  const handleAddToDictionary = async (): Promise<void> => {
    if (!explorationResult) return;

    try {
      setAddingToDictionary(true);
      const conversationLanguage: string | null = localStorage.getItem(
        "conversation_language"
      );

      if (!conversationLanguage) {
        throw new Error("Conversation language not found");
      }

      // Use first example for dictionary, if available
      const firstExample = explorationResult.examples?.[0] || {};
      await addWordToDictionary({
        word_base_form: explorationResult.base_form,
        word_translation: explorationResult.translation,
        grammar: explorationResult.grammar,
        example_sentence: firstExample.example || "",
        example_sentence_translation: firstExample.example_translation || "",
        word_original_language: conversationLanguage,
      });

      setAddedToDictionary(true);
    } catch (error: any) {
      console.error("Error adding word to dictionary:", error);
      if (error.message.includes("already have this word")) {
        alert("This word is already in your dictionary!");
      } else {
        alert("Failed to add word to dictionary. Please try again.");
      }
    } finally {
      setAddingToDictionary(false);
    }
  };

  return (
    <div className="word-explorer relative">
      <div className="text-xl font-bold text-white mb-2">{clickedWord}</div>
      {/* Close button */}
      {onClose && (
        <button
          className="absolute top-0 right-0 btn btn-xs btn-circle btn-ghost hover:btn-error text-black"
          aria-label="Close word explorer"
          onClick={onClose}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      {/* Loading state */}
      {!explorationResult && !error && (
        <div className="flex items-center justify-center py-4">
          <span className="loading loading-spinner loading-sm text-black"></span>
          <span className="ml-2 text-black/80">Loading...</span>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="alert alert-error alert-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Exploration result */}
      {explorationResult && !error && (
        <div className="exploration-result space-y-2">
          <div className="text-white/95 font-medium">
            <span className="text-white font-semibold text-lg">{explorationResult.translation}</span>
            <span className="text-white/70"> ({explorationResult.base_form}, {explorationResult.grammar})</span>
          </div>
          {explorationResult.examples?.map((ex, idx) => (
            <React.Fragment key={idx}>
              <div className="text-white/85 pl-3 border-l-2 border-white/30">{ex.example}</div>
              {ex.example_translation && (
                <div className="italic text-white/70 pl-3 text-sm">{ex.example_translation}</div>
              )}
            </React.Fragment>
          ))}
          <div className="mt-4">
            <button
              onClick={handleAddToDictionary}
              disabled={addingToDictionary || addedToDictionary}
              className={`btn btn-sm flex items-center gap-2 ${
                addedToDictionary
                  ? "btn-success"
                  : "btn-primary"
              }`}
            >
              {addingToDictionary ? (
                <>
                  <span className="loading loading-spinner loading-xs"></span>
                  Adding...
                </>
              ) : addedToDictionary ? (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-success"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span>Added!</span>
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <span>Add to Dictionary</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordExplorer;
